import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
} from './ui/alert-dialog';

// Warn at 5 minutes idle, log out at 10 minutes idle.
const WARNING_MS = 5 * 60 * 1000;
const LOGOUT_MS = 10 * 60 * 1000;

const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'wheel', 'touchstart', 'scroll'];

export default function InactivityGuard() {
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.ceil((LOGOUT_MS - WARNING_MS) / 1000));

  // Mirrors showWarning for the activity listeners, which are attached once
  // and would otherwise read a stale value from their original closure.
  const showWarningRef = useRef(false);

  const warningTimer = useRef(null);
  const logoutTimer = useRef(null);
  const countdownInterval = useRef(null);

  const clearTimers = useCallback(() => {
    clearTimeout(warningTimer.current);
    clearTimeout(logoutTimer.current);
    clearInterval(countdownInterval.current);
  }, []);

  const setWarningVisible = useCallback((visible) => {
    showWarningRef.current = visible;
    setShowWarning(visible);
  }, []);

  const handleLogout = useCallback(() => {
    clearTimers();
    setWarningVisible(false);
    logout();
    navigate('/login');
  }, [clearTimers, setWarningVisible, logout, navigate]);

  const startTimers = useCallback(() => {
    clearTimers();
    setWarningVisible(false);

    warningTimer.current = setTimeout(() => {
      setSecondsLeft(Math.ceil((LOGOUT_MS - WARNING_MS) / 1000));
      setWarningVisible(true);
      countdownInterval.current = setInterval(() => {
        setSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    }, WARNING_MS);

    logoutTimer.current = setTimeout(handleLogout, LOGOUT_MS);
  }, [clearTimers, setWarningVisible, handleLogout]);

  // Reset the idle clock on genuine user activity. Ignored while the
  // warning dialog is up — dismissing it requires the explicit
  // "Stay signed in" action, not just an incidental mouse move.
  const handleActivity = useCallback(() => {
    if (showWarningRef.current) return;
    startTimers();
  }, [startTimers]);

  useEffect(() => {
    if (!isAuthenticated) {
      clearTimers();
      setWarningVisible(false);
      return;
    }

    startTimers();
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, handleActivity));

    return () => {
      clearTimers();
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, handleActivity));
    };
  }, [isAuthenticated, startTimers, handleActivity, clearTimers, setWarningVisible]);

  const handleStaySignedIn = () => {
    startTimers();
  };

  if (!isAuthenticated) return null;

  return (
    <AlertDialog open={showWarning}>
      <AlertDialogContent data-testid="inactivity-warning-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Still there?</AlertDialogTitle>
          <AlertDialogDescription>
            You've been inactive for a while. For your security, you'll be signed out in{' '}
            <span className="font-semibold text-slate-900">{secondsLeft}s</span> unless you
            stay signed in.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={handleStaySignedIn} data-testid="inactivity-stay-signed-in">
            Stay signed in
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
